import React, { useState, useRef, forwardRef, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import moment from 'moment';
import InputMask from 'react-input-mask';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import Alert from 'react-s-alert';

const formatDate = (date, def = '', format = 'YYYY-MM-DD') => {
  try {
    const endDate = moment(date, 'YYYY-MM-DD HH:mm').format(format);

    if (_.isEmpty(date) && date === '') {
      return endDate === '';
    }

    if (endDate === 'Invalid date') {
      Alert.warning('Entered date is invalid');
    }

    const newDate = endDate === 'Invalid date' ? '' : endDate;

    return newDate;
  } catch (error) {
    return def;
  }
};

const CustomRender = forwardRef(({ onClick }, ref) => (
  <button
    type="button"
    ref={ref}
    className="btn btn-secondary btn-sm button-date"
    onClick={onClick}
  >
    <i className="fa fa-lg fa-calendar" />
  </button>
));

CustomRender.propTypes = {
  onClick: PropTypes.instanceOf(Function),
};

CustomRender.defaultProps = {
  onClick: () => {},
};

const renderDateFormat = (showMonthYearPicker, withTime) => {
  if (showMonthYearPicker) {
    return 'YYYY-MM';
  }
  if (withTime) {
    return 'YYYY-MM-DD HH:mm';
  }
  return 'YYYY-MM-DD';
};

const inputMaskRenderFormat = (showMonthYearPicker, withTime) => {
  if (showMonthYearPicker) {
    return '9999-99';
  }
  if (withTime) {
    return '9999-99-99 99:99';
  }
  return '9999-99-99';
};

const renderPlaceHolder = (data, showMonthYearPicker, withTime) => {
  let placeHolder = '';
  if (data.length > 0) {
    return data;
  }

  placeHolder = () => {
    if (showMonthYearPicker) {
      return 'YYYY-MM';
    }
    if (withTime) {
      return 'YYYY-MM-DD HH:MM';
    }
    return 'YYYY-MM-DD';
  };

  return placeHolder();
};

function DateInputPicker({
  disabled,
  showMonthYearPicker,
  withTime,
  isRequired,
  placeholder,
  onChange,
  maxDate,
  minDate,
  value,
  openToDate,
}) {
  const [selected, setSelected] = useState(null);
  const customRenderRef = useRef();

  const handleChangeInput = (e) => {
    setSelected(e.target.value);
  };

  const handleChangePicker = (e) => {
    const newVal = formatDate(
      e,
      '',
      renderDateFormat(showMonthYearPicker, withTime)
    );
    onChange({ target: { value: newVal } });
  };

  const handleUpdateActual = (e) => {
    const newVal = formatDate(
      e.target.value,
      selected,
      renderDateFormat(showMonthYearPicker, withTime)
    );
    if (new Date(newVal) < maxDate && new Date(newVal) >= minDate) {
      setSelected(newVal);
      onChange({ target: { value: newVal } });
      return;
    }

    Alert.warning('Entered date is invalid');
    setSelected('');
    onChange({ target: { value: '' } });
  };

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <div className="input-group">
      <InputMask
        onChange={handleChangeInput}
        onBlur={handleUpdateActual}
        maskChar={null}
        disabled={disabled}
        required={isRequired}
        placeholder={renderPlaceHolder(
          placeholder,
          showMonthYearPicker,
          withTime
        )}
        mask={inputMaskRenderFormat(showMonthYearPicker, withTime)}
        className="form-control form-control-date"
        value={selected || ''}
      />
      <span className="input-group-btn">
        <DatePicker
          customInput={<CustomRender ref={customRenderRef} />}
          disabled={disabled}
          showYearDropdown
          timeIntervals={15}
          showMonthYearPicker={showMonthYearPicker}
          showTimeSelect={withTime}
          timeFormat="HH:mm"
          maxDate={maxDate}
          minDate={minDate}
          openToDate={!_.isEmpty(value) ? new Date(selected) : openToDate}
          popperPlacement="left-start"
          selected={new Date(value || openToDate)}
          onChange={handleChangePicker}
        />
      </span>
    </div>
  );
}

DateInputPicker.defaultProps = {
  disabled: false,
  showMonthYearPicker: false,
  withTime: false,
  isRequired: false,
  placeholder: '',
  maxDate: new Date('3000-01-01'),
  minDate: new Date('1900-01-01'),
  openToDate: new Date(),
  value: '',
};

DateInputPicker.propTypes = {
  disabled: PropTypes.bool,
  showMonthYearPicker: PropTypes.bool,
  withTime: PropTypes.bool,
  isRequired: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.instanceOf(Function).isRequired,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  openToDate: PropTypes.instanceOf(Date),
  value: PropTypes.string,
};

const mapStateToProps = () => ({});

const enhance = _.flowRight([withRouter, connect(mapStateToProps)]);

export default enhance(DateInputPicker);
